import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { FilterTypes, Select } from "@cofano/cck";

import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import CharterTypeSelect from "view/charters/components/selects/CharterTypeSelect";
import SalesCharterStatusSelect, { type SalesCharterStatusSelectOption } from "view/charters/components/selects/SalesCharterStatusSelect";
import InvoiceGroupTypeSelect, { type InvoiceGroupTypeSelectOption } from "view/invoicegrouptypes/components/selects/InvoiceGroupTypeSelect";
import RelationSelect, { type RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import ShipSelect, { type ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import StatusSelect, { type StatusSelectOption } from "view/statuses/components/selects/StatusSelect";

import { DataFormat } from "imports/Utils";
import { FormattedDateWindow } from "utils/DateUtils";

import { CharterSubtype, CharterType } from "__generated__/enums";

const useDeclarationFilters = (charterType: CharterType | null) => {
	const session = useStoreState((store) => store.session);

	return {
		filterFields: {
			CharterNumber: {
				id: "charterNumber",
				title: intl.formatMessage({ id: "CHARTER" }),
				filterType: FilterTypes.TEXT,
			},
			SalesCharter: {
				id: "salesCharter",
				title: intl.formatMessage({ id: "SALES_CHARTER" }),
				filterType: FilterTypes.TEXT,
			},
			PurchaseCharter: {
				id: "purchaseCharter",
				title: intl.formatMessage({ id: "PURCHASE_CHARTER" }),
				filterType: FilterTypes.TEXT,
			},
			ShipName: {
				id: "ship",
				title: intl.formatMessage({ id: "SHIP" }),
				render: (value: ShipSelectOption[] | null, onChange: (options: ShipSelectOption[] | null) => void) => (
					<ShipSelect value={value} onChange={onChange} multiple />
				),
				renderFilterValue: (value: ShipSelectOption[] | null | undefined) => (value ?? []).map(o => o.name).join(", "),
			},
			CustomerReference: {
				id: "customerReference",
				title: intl.formatMessage({ id: "CUSTOMER_REFERENCE" }),
				filterType: FilterTypes.TEXT,
			},
			DossierNumber: {
				id: "dossierNumber",
				title: intl.formatMessage({ id: "DOSSIER_NUMBER" }),
				filterType: FilterTypes.TEXT,
			},
			Creditor: {
				id: "creditor",
				title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
				render: (value: RelationSelectOption[] | null, onChange: (options: RelationSelectOption[] | null) => void) => (
					<RelationSelect value={value} onChange={onChange} multiple />
				),
				renderFilterValue: (value: RelationSelectOption[] | null | undefined) => (value ?? []).map(o => o.name).join(", "),
			},
			Debtor: {
				id: "debtor",
				title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
				render: (value: RelationSelectOption[] | null, onChange: (options: RelationSelectOption[] | null) => void) => (
					<RelationSelect value={value} onChange={onChange} multiple />
				),
				renderFilterValue: (value: RelationSelectOption[] | null | undefined) => (value ?? []).map(o => o.name).join(", "),
			},
			DeclarationStatus: {
				id: "declarationStatus",
				title: intl.formatMessage({ id: "DECLARATION_STATUS" }),
				render: (value: StatusSelectOption[] | null, onChange: (options: StatusSelectOption[] | null) => void) => (
					<StatusSelect
						statuses={session.statuses?.DECLARATION ?? []}
						value={value ?? undefined}
						onChange={(options) => onChange(options ?? null)}
						multiple />
				),
				renderFilterValue: (value: StatusSelectOption[] | null | undefined) => (value ?? []).map(s => DataFormat.status(s, session.locale)).join(", "),
			},
			SalesCharterStatus: {
				id: "salesCharterStatus",
				title: `${intl.formatMessage({ id: "CHARTER" })} ${intl.formatMessage({ id: "STATUS" }).toLowerCase()}`,
				render: (value: SalesCharterStatusSelectOption[] | null, onChange: (options: SalesCharterStatusSelectOption[] | null) => void) => (
					<SalesCharterStatusSelect value={value} onChange={onChange} multiple />
				),
				renderFilterValue: (value: SalesCharterStatusSelectOption[] | null | undefined) => (value ?? []).map(s => intl.formatMessage({ id: `${s.languageKeyPrefix}${s.name}` })).join(", "),
			},
			CharterType: {
				id: "charterType",
				title: intl.formatMessage({ id: "CHARTER_TYPE" }),
				render: (value: CharterType | null, onChange: (option: CharterType | null) => void) => (
					<CharterTypeSelect value={value} onChange={onChange} />
				),
				renderFilterValue: (value: CharterType | null | undefined) => value ? intl.formatMessage({ id: value }) : "",
			},
			CharterSubtype: {
				id: "charterSubtype",
				title: `${intl.formatMessage({ id: "CHARTER" })} ${intl.formatMessage({ id: "SUBTYPE" }).toLowerCase()}`,
				render: (value: CharterSubtype[] | null, onChange: (options: CharterSubtype[] | null) => void) => (
					<CharterSubtypeSelect
						value={value}
						onChange={onChange}
						customCharterSubtypes={Object.values(CharterSubtype)}
						multiple />
				),
				renderFilterValue: (value: CharterSubtype[] | null | undefined) => (value ?? []).map(s => intl.formatMessage({ id: s })).join(", "),
			},
			BlDate: {
				id: "blDate",
				title: intl.formatMessage({ id: "BL_DATE" }),
				filterType: FilterTypes.DATE_RANGE,
				renderFilterValue: (value: [number, number] | null | undefined) => value ? <FormattedDateWindow from={value[0]} to={value[1]} format="ll" /> : null,
			},
			Window: {
				id: "window",
				title: intl.formatMessage({ id: "WINDOW" }),
				filterType: FilterTypes.DATE_RANGE,
				renderFilterValue: (value: [number, number] | null | undefined) => value ? <FormattedDateWindow from={value[0]} to={value[1]} format="ll" /> : null,
			},
			Declared: {
				id: "declared",
				title: intl.formatMessage({ id: "DECLARED" }),
				render: (value: { label: string; value: boolean } | null, onChange: (option: { label: string; value: boolean } | null) => void) => (
					<Select
						value={value}
						fetchOptions={[
							{ label: intl.formatMessage({ id: "YES" }), value: true },
							{ label: intl.formatMessage({ id: "NO" }), value: false },
						]}
						getOptionLabel={(o: { label: string; value: boolean }) => o.label}
						onChange={(o) => onChange(o ?? null)} />
				),
				renderFilterValue: (value: { label: string } | null | undefined) => value?.label ?? "",
			},
			Invoiced: {
				id: "invoiced",
				title: intl.formatMessage({ id: "INVOICED" }),
				filterType: FilterTypes.BOOLEAN,
				renderFilterValue: (value: boolean | null | undefined) => DataFormat.booleanAsText(value),
			},
			InvoiceGroupType: {
				id: "invoiceGroupType",
				title: intl.formatMessage({ id: "INVOICE_GROUP_TYPE" }),
				render: (value: InvoiceGroupTypeSelectOption[] | null, onChange: (options: InvoiceGroupTypeSelectOption[] | null) => void) => (
					<InvoiceGroupTypeSelect
						value={value}
						charterType={charterType}
						onChange={onChange}
						includeAdvancePayment
						includeParentContractExclusive
						multiple
					/>
				),
				renderFilterValue: (value: InvoiceGroupTypeSelectOption[] | null | undefined) => (value ?? []).map(o => o.name).join(", "),
			},
		},
	};
};

export default useDeclarationFilters;
